import {
  Component,
  ContentChild,
  inject,
  OnInit,
  output,
  TemplateRef,
  effect, computed, model, ChangeDetectorRef
} from "@angular/core";
import {MatButtonModule} from "@angular/material/button";
import {MatIconModule} from "@angular/material/icon";
import {MatPaginatorModule, PageEvent} from "@angular/material/paginator";
import {MatSortModule, Sort} from "@angular/material/sort";
import {MatTableModule} from "@angular/material/table";
import {GridModel} from "../../model/page-grid";
import {CommonModule} from "@angular/common";
import {ExportGridService} from "../../service/files/export-grid.service";
import {ChunkedExportService} from "../../service/files/chunked-export.service";
import {FileService} from "../../service/files/file.service";
import {PageGridFilterComponent} from "../page-grid-filter/page-grid-filter.component";
import {ModalService} from "../../service/modal/modal.service";
import {NavService} from "src/app/nav/upload/service/nav.service";
import {Subscription} from "rxjs";
import {AutoUnsubscribe} from "src/infrastructure/decorators/auto-unsubscribe.decorator";
import {PipesModule} from "src/infrastructure/pipes/pipes.module";
import {MatSnackBar} from "@angular/material/snack-bar";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";
import {MatTooltipModule} from "@angular/material/tooltip";
import moment from 'moment';

@AutoUnsubscribe()
@Component({
    selector: 'page-grid',
    templateUrl: './page-grid.component.html',
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        MatTableModule,
        MatPaginatorModule,
        MatSortModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        PipesModule,
    ]
})
export class PageGridComponent<T = any> implements OnInit {

    @ContentChild('actionItem')
    protected actionItem?: TemplateRef<any>;
    public gridConfig = model.required<GridModel<T>>();
    public localGridConfig = computed(() => this.gridConfig());

    protected gridClickColumn = output<{column: string, item: any}>();
    protected gridCurrentData = output<unknown[]>();
    protected gridEvent = output<boolean>();
    protected gridSecondEvent = output<boolean>();

    private exportGridService = inject(ExportGridService);
    private chunkedExportService = inject(ChunkedExportService);
    private fileService = inject(FileService);
    private modalService = inject(ModalService);
    private navService = inject(NavService);
    private snackBar = inject(MatSnackBar);

    protected exportProgress$ = this.chunkedExportService.exportProgress$;
    protected isExporting$ = this.chunkedExportService.isExporting$;
    protected displayedColumns: string[] = [];
    public items: unknown[] = [];
    public showTotals = false;

    public filters: any = {};
    protected pagination: Partial<PageEvent & {totalItemCount?: number}> = {};
    protected sorting: Partial<Sort> = {};
    protected title = '';

    private exportLoggingDone = false;

    protected filterButtonEvent$?: Subscription;
    getRowClass(row: any): string {
        const classFn = this.localGridConfig?.().rowClass;
        if (classFn) {
            return classFn(row) || '';
        }
        return '';
    }

    getRowStyle(row: any): Record<string, string> {
        const styleFn = this.localGridConfig?.().rowStyle;
        if (styleFn) {
            return styleFn(row) || {};
        }
        return {};
    }

    getRowTooltip(row: any): string {
        const tooltipFn = this.localGridConfig?.().rowTooltip;
        if (tooltipFn) {
            return tooltipFn(row) || '';
        }
        return '';
    }

    hasRowTooltip(row: any): boolean {
        const tooltipFn = this.localGridConfig?.().rowTooltip;
        return tooltipFn ? !!tooltipFn(row) : false;
    }

  constructor(private cdr: ChangeDetectorRef) {
      effect(() => {
        const config = this.gridConfig();
        this.displayedColumns = config.columns
          .filter(column => !column.shouldHideColumn)
          .map(column => column.columnProperty);

        if(config.showActionColumn){
          this.displayedColumns.push('actions');
        }
      });
    }

    ngOnInit(): void {
      this.loadConfig();
    }

    private loadConfig() {
      this.pagination = {
        pageSize: this.gridConfig().defaultPageSize || 25,
        pageIndex: 0,
        totalItemCount: 0,
      };

      this.filterButtonEvent$ = this.navService.getFilterButtonEvent().subscribe(()=>{
        this.modalService.open(PageGridFilterComponent, {
          data: {
            filters: this.gridConfig().filters,
            currentValue: this.filters,
            singleColumn: this.gridConfig().singleColumnFilter,
            dataTestId: this.gridConfig().gridDataTestId,
          },
                }).afterClosed().subscribe((filter: unknown)=>{
                    if (filter === undefined) {
                        return;
                    }

                    const modalFilterKeys = new Set<string>();
                    (this.gridConfig().filters || []).forEach(f => {
                        modalFilterKeys.add(f.filterProperty);
                        if (f.lastFilterProperty) modalFilterKeys.add(f.lastFilterProperty);
                    });

                    const preservedNonModalFilters: Record<string, any> = {};
                    Object.entries(this.filters || {}).forEach(([k, v]) => {
                        if (!modalFilterKeys.has(k)) preservedNonModalFilters[k] = v;
                    });

                    if (filter && typeof filter === 'object' && Object.keys(filter).length > 0) {
                        const meaningfulFilters: any = {};
                        let hasAnyMeaningfulFilter = false;

                        for (const [key, value] of Object.entries(filter as any)) {
                            if (value !== null && value !== undefined && value !== '') {
                                if (typeof value === 'string' && value.trim() !== '') {
                                    meaningfulFilters[key] = value.trim();
                                    hasAnyMeaningfulFilter = true;
                                } else if (typeof value === 'object' && value instanceof Date) {
                                    meaningfulFilters[key] = value;
                                    hasAnyMeaningfulFilter = true;
                                } else if (typeof value === 'object' && value &&
                                    (("value" in value && (value as any).value) ||
                                     ("id" in value && (value as any).id))) {
                                    meaningfulFilters[key] = value;
                                    hasAnyMeaningfulFilter = true;
                                } else if (typeof value !== 'object' && value !== '') {
                                    meaningfulFilters[key] = value;
                                    hasAnyMeaningfulFilter = true;
                                }
                            }
                        }

                        this.filters = hasAnyMeaningfulFilter ? { ...preservedNonModalFilters, ...meaningfulFilters } : { ...preservedNonModalFilters };
                    } else {
                        this.filters = { ...preservedNonModalFilters };
                    }

                    this.pagination = {
                        pageSize: this.pagination.pageSize || 25,
                        pageIndex: 0,
                        totalItemCount: 0,
                    };
                    this.title = this.gridConfig().gridTitleFunction?.(this.filters) ?? this.gridConfig().gridTitle;
                    this.getItems();
                });
      });

      this.filters = this.gridConfig().filtersInitial ?? {};

      this.title = this.gridConfig().gridTitleFunction?.(this.filters) ?? this.gridConfig().gridTitle;

      this.sorting = this.gridConfig().defaultSort || {
        active: 'id',
        direction: 'desc'
      };

      this.getItems();
    }

  public getItems(): void {
        const currentPagination = { ...this.pagination };
        const currentSort = { ...this.sorting };

        this.gridConfig().gridGetService.getItems({
            filters: this.filters,
            page: this.pagination,
            sort: this.sorting,
        }).subscribe({
            next: (data) => {
                this.items = data.items;
                this.gridCurrentData.emit(this.items);

                this.pagination = {
                    ...currentPagination,
                    pageIndex: data.pagination.pageNumber - 1,
                    totalItemCount: data.pagination.totalItemCount,
                };

                if(!this.gridConfig().isTotalsRowHidden && data?.totals?.length) {
                    const columnsWithTotals = this.localGridConfig().columns.map((value)=>{
                        if (this.shouldExcludeFromTotals(value.columnProperty)) {
                            return {
                                ...value
                            };
                        }

                        const columnTotal = data.totals?.find(
                            (total) => total.columnName.toLowerCase() === value.columnProperty.toLocaleLowerCase())?.total;

                        if(columnTotal !== undefined && columnTotal !== null) {
                            this.showTotals = true;
                            return {
                                ...value,
                                columnTotal,
                            };
                        } else {
                            return {
                                ...value
                            };
                        }
                    });

                    this.gridConfig.update(prevValue => ({...prevValue, columns: columnsWithTotals}));
                }
                else {
                    this.showTotals = false;
                }
                this.sorting = currentSort;
            },
            error: () => {
            }
        });
    this.cdr.detectChanges();
    }
    protected onSortChanged(sort: Sort): void {
        this.sorting = { ...sort };

        if(!this.sorting?.active || !this.sorting?.direction) {
          const defaultActive = this.gridConfig().defaultSort?.active || '';
          const defaultDirection = this.gridConfig().defaultSort?.direction || '';

          this.sorting = {
            active: defaultActive,
            direction: defaultDirection
          };
        }

        this.getItems();
    }
    protected onPageChanged(page: PageEvent): void {
        this.pagination = {
            ...this.pagination,
            ...page,
        };

        this.getItems();
    }

    protected onEventButton(): void {
        this.gridEvent.emit(true);
    }

    protected onSecondEventButton(): void {
        this.gridSecondEvent.emit(true);
    }

    protected onClickColumn(column:string, item:unknown): void {
        this.gridClickColumn.emit({
            column,
            item,
        });
    }

    protected checkPreventDefault(shouldNotPrevent: boolean, event:any): void {
        if(!shouldNotPrevent){
            event.preventDefault();
        }
    }
    protected downloadGridToExcel(): void {
        this.exportLoggingDone = false;

        const currentSorting: Partial<Sort> = {
            active: this.sorting?.active || this.gridConfig().defaultSort?.active || 'id',
            direction: this.sorting?.direction || this.gridConfig().defaultSort?.direction || 'desc'
        };

        const service = this.gridConfig().gridGetService as any;

        if (service && service.baseUrl && service.baseUrl !== 'dbov2/adx-booking') {
            const baseUrl = service.baseUrl;

            let sortProperty = currentSorting.active || 'id';
            const sortDirection = currentSorting.direction?.toUpperCase() || 'DESC';

            if (service.mapSortProperty && typeof service.mapSortProperty === 'function') {
                sortProperty = service.mapSortProperty(sortProperty) || sortProperty;
            }

            let rawFilters = JSON.parse(JSON.stringify(this.filters || {}));

            if (service.getCurrentFilters && typeof service.getCurrentFilters === 'function') {
                const currentFilters = service.getCurrentFilters();
                if (currentFilters && Object.keys(currentFilters).length > 0) {
                    rawFilters = { ...currentFilters };
                }
            }

            let formattedFilters = { ...rawFilters };

            if (service.getAutocompleteValue && typeof service.getAutocompleteValue === 'function') {
                Object.keys(rawFilters).forEach(key => {
                    if (key.toLowerCase().includes('date') && rawFilters[key] instanceof Date) {
                        const dateFormat = service.DATE_FORMAT || 'YYYY-MM-DD';
                        try {
                            if (dateFormat.includes('[Z]')) {
                                formattedFilters[key] = moment.utc(rawFilters[key]).format(dateFormat);
                            } else {
                                formattedFilters[key] = moment(rawFilters[key]).format(dateFormat);
                            }
                        } catch (e) {
                        }
                    } else if (rawFilters[key] && typeof rawFilters[key] === 'object' && 'value' in rawFilters[key]) {
                        formattedFilters[key] = service.getAutocompleteValue(rawFilters[key]);
                    }
                });
            }
            const exportFilters = {
                ...formattedFilters,
                pageNumber: 1,
                PageNumber: 1,

                propertyName: sortProperty,
                PropertyName: sortProperty,
                orderKey: sortDirection,
                OrderKey: sortDirection
            };

            if (baseUrl.includes('fms-trams-trips')) {
                if (formattedFilters.fmsTripStartDate) {
                    exportFilters.FmsTripStartDateFrom = moment(formattedFilters.fmsTripStartDate).format('YYYY-MM-DD');
                    exportFilters.fmsTripStartDateFrom = moment(formattedFilters.fmsTripStartDate).format('YYYY-MM-DD');
                }
                if (formattedFilters.fmsTripEndDate) {
                    exportFilters.FmsTripStartDateTo = moment(formattedFilters.fmsTripEndDate).format('YYYY-MM-DD');
                    exportFilters.fmsTripStartDateTo = moment(formattedFilters.fmsTripEndDate).format('YYYY-MM-DD');
                }
                if (formattedFilters.adxTripReference) {
                    exportFilters.AdxTripReference = formattedFilters.adxTripReference;
                }
                if (formattedFilters.tramsRecordLocator) {
                    exportFilters.TramsRecordLocator = formattedFilters.tramsRecordLocator;
                }
                if (formattedFilters.fmsDataSourceName) {
                    exportFilters.FmsDataSourceName = formattedFilters.fmsDataSourceName;
                }
            }
            if (typeof service.getExportFilters === 'function') {
                Object.assign(exportFilters, service.getExportFilters(formattedFilters));
            }
            if (baseUrl.includes('fms-invoice/search')) {
              if(formattedFilters.issueDateFrom) {
                exportFilters.issueDateFrom = moment(formattedFilters.issueDateFrom).format('YYYY-MM-DD');
              }
              if(formattedFilters.issueDateTo) {
                exportFilters.issueDateTo = moment(formattedFilters.issueDateTo).format('YYYY-MM-DD');
              }
            }
            if (baseUrl.includes('vendor-statements') || baseUrl.includes('vendor-balances') || baseUrl.includes('client-statements') || baseUrl.includes('client-balances')) {
                if(formattedFilters.asOfDate) {
                    exportFilters.asOfDate = moment(formattedFilters.asOfDate).format('YYYY-MM-DD');
                }
            }
            if (baseUrl.includes('adx-payment-detail/search')) {
                if(formattedFilters.invoiceId) {
                    exportFilters.adxInvoiceId = formattedFilters.invoiceId;
                }
            }

            const mapFunction = (data: unknown) => this.formatExportRecord(data);

            this.exportGridService.getByDataSource(
                baseUrl,
                this.gridConfig().gridDataTestId,
                exportFilters,
                mapFunction,
                this.pagination.totalItemCount
            );
        } else {
            const exportSort: Partial<Sort> = {
                active: currentSorting.active,
                direction: currentSorting.direction
            };

            const maxRows = this.fileService.getMaxRowsPerFile();

            if (this.pagination.totalItemCount && this.pagination.totalItemCount > maxRows) {
                this.snackBar.open(
                    'Sorry, amount of data is too large to generate file. Please use filters',
                    'Dismiss',
                    { duration: 8000 }
                );
                return;
            }

            let effectiveFilters = JSON.parse(JSON.stringify(this.filters || {}));
            if (service && service.getCurrentFilters && typeof service.getCurrentFilters === 'function') {
                const currentFilters = service.getCurrentFilters();
                if (currentFilters && Object.keys(currentFilters).length > 0) {
                    effectiveFilters = { ...currentFilters };
                }
            }

            this.gridConfig().gridGetService.getItems({
                filters: effectiveFilters,
                page: {
                    pageSize: maxRows,
                    pageIndex: 0,
                },
                sort: exportSort,
            }).subscribe({
                next: (data) => {
                    this.exportGridService.downloadDataSource(
                        data,
                        this.gridConfig().gridDataTestId,
                        (data) => this.formatExportRecord(data)
                    );
                },
                error: () => {
                    this.snackBar.open('Failed to export data. Try using filters to reduce data size.', 'Dismiss');
                }
            });
        }
    }

    protected downloadGridToPdf(): void {
        if(this.gridConfig().gridGetService?.getPdf){
            this.gridConfig().gridGetService.getPdf?.({
                filters: this.filters,
                page: { pageSize: this.pagination.totalItemCount },
                sort: this.sorting,
            }).subscribe();
        }
    }

    private formatExportRecord(data: unknown): Record<string, unknown> {
        if (!data) {
            return {};
        }

        const record = data as Record<string, unknown>;
        let exportObject = {} as Record<string, unknown>;

        const visibleColumns = this.gridConfig().columns.filter(col =>
            (this.displayedColumns.includes(col.columnProperty) || col.columnProperty === 'isEligible') &&
            col.columnProperty !== 'actions');

        if (!this.exportLoggingDone) {
            this.exportLoggingDone = true;
        }

        visibleColumns.forEach((column) => {
            if (column.columnProperty === 'actions') {
                return;
            }

            try {
                let currentRecord: unknown = undefined;

                if (column.columnPropertyFunction) {
                    currentRecord = column.columnPropertyFunction(record);
                } else if (column.columnProperty && column.columnProperty.includes('.')) {
                    const props = column.columnProperty.split('.');
                    let nestedValue: any = record;

                    for (const prop of props) {
                        if (nestedValue && typeof nestedValue === 'object') {
                            nestedValue = nestedValue[prop];
                        } else {
                            nestedValue = undefined;
                            break;
                        }
                    }

                    currentRecord = nestedValue;
                } else {
                    currentRecord = record[column.columnProperty];
                }

                if (column.processData) {
                    currentRecord = column.processData(currentRecord as any);
                }

                exportObject[column.columnHeader] = currentRecord;
            } catch (error) {
                exportObject[column.columnHeader] = '';
            }
        });

        return exportObject;
    }

    /**
     * Checks if a column should be excluded from totals calculation
     * @param columnProperty The column property name
     * @returns True if the column should be excluded from totals
     */
    private shouldExcludeFromTotals(columnProperty: string): boolean {
        const excludedColumns = [
            'currentSplitRate',
            'splitRate',
            'marginPercentage',
            'agentSplit',
            'agencySplit'
        ];

        return excludedColumns.includes(columnProperty);
    }
}
